import { useEffect, useRef, useState } from "react";

/**
 * Cartoony eyeballs that follow the user's cursor.
 * Two big white eyes with thick black outline, blue iris, black pupil.
 * Pupils translate inside the iris based on cursor position.
 * Occasionally blinks.
 */
export default function EyeballMascot() {
  const wrapRef = useRef(null);
  const [pupils, setPupils] = useState({ x: 0, y: 0 });
  const [blink, setBlink] = useState(false);

  useEffect(() => {
    let raf = 0;
    const handler = (e) => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const el = wrapRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const dx = e.clientX - cx;
        const dy = e.clientY - cy;
        const dist = Math.min(1, Math.hypot(dx, dy) / 320);
        const angle = Math.atan2(dy, dx);
        const maxOffset = 12;
        setPupils({
          x: Math.cos(angle) * maxOffset * dist,
          y: Math.sin(angle) * maxOffset * dist,
        });
      });
    };
    window.addEventListener("mousemove", handler);
    return () => {
      window.removeEventListener("mousemove", handler);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  useEffect(() => {
    let timer;
    const scheduleBlink = () => {
      const delay = 2500 + Math.random() * 4000;
      timer = setTimeout(() => {
        setBlink(true);
        setTimeout(() => setBlink(false), 140);
        scheduleBlink();
      }, delay);
    };
    scheduleBlink();
    return () => clearTimeout(timer);
  }, []);

  const Eye = ({ extra = 0 }) => (
    <svg width="120" height="120" viewBox="0 0 120 120" className="wiggle">
      {/* White eyeball */}
      <ellipse
        cx="60"
        cy="60"
        rx="52"
        ry={blink ? 4 : 52}
        fill="#FFFFFF"
        stroke="#0A0B0E"
        strokeWidth="5"
        style={{ transition: "ry 90ms ease" }}
      />
      {!blink && (
        <>
          {/* Blue iris */}
          <circle
            cx={60 + pupils.x + extra}
            cy={60 + pupils.y}
            r="22"
            fill="#4CC9F0"
            stroke="#0A0B0E"
            strokeWidth="3.5"
          />
          {/* Black pupil */}
          <circle
            cx={60 + pupils.x + extra}
            cy={60 + pupils.y}
            r="10"
            fill="#0A0B0E"
          />
          {/* Shine */}
          <circle
            cx={60 + pupils.x + extra - 5}
            cy={60 + pupils.y - 6}
            r="4"
            fill="#FFFFFF"
          />
          <circle
            cx={60 + pupils.x + extra + 6}
            cy={60 + pupils.y + 4}
            r="1.6"
            fill="#FFFFFF"
            opacity="0.8"
          />
        </>
      )}
    </svg>
  );

  return (
    <div
      ref={wrapRef}
      className="relative flex items-center justify-center gap-4 select-none pointer-events-none"
      data-testid="eyeball-mascot"
      aria-hidden="true"
    >
      <Eye />
      <Eye />
    </div>
  );
}
